import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { api } from '../hooks/useApi';
import { formatCurrency, formatDate } from '../lib/utils';
import { useFeatures } from '../lib/features';
import { mxnPayUrl } from '../lib/payLinks';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Receipt, Trash2, Archive, ChevronDown, ChevronUp, Download, Mail, ExternalLink, AlertTriangle, ChevronsUpDown, Check, History, RefreshCw, Plus, FileCheck, SquarePlus, SquareMinus } from 'lucide-react';
import { UninvoicedBookingsTable } from '../components/UninvoicedBookingsTable';
import { EditionBulkBookingsTable } from '../components/EditionBulkBookingsTable';
import { InvoiceDetailsRow } from '../components/InvoiceDetailsRow';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const invoiceStatusColors = {
  draft: 'bg-slate-900/50 text-slate-400 border border-slate-700',
  sent: 'bg-blue-900/50 text-blue-400 border border-blue-800',
  paid: 'bg-emerald-900/50 text-emerald-400 border border-emerald-800',
  unpaid: 'bg-orange-900/50 text-orange-400 border border-orange-800',
  overdue: 'bg-red-900/50 text-red-400 border border-red-800',
  cancelled: 'bg-red-900/50 text-red-400 border border-red-800',
};

const invoiceStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

const AccountingPage = () => {
  const { multiCurrency } = useFeatures();
  const [advertisers, setAdvertisers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deletedInvoices, setDeletedInvoices] = useState([]);
  const [showDeletedSection, setShowDeletedSection] = useState(false);

  // Mode: "client" (existing) or "edition" (new bulk-by-edition)
  const [createMode, setCreateMode] = useState('client');
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('');
  const [selectedEdition, setSelectedEdition] = useState('');
  const [editionBookings, setEditionBookings] = useState([]);
  const [editionSelectedIds, setEditionSelectedIds] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);

  const [uninvoicedBookings, setUninvoicedBookings] = useState([]);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [sendByEmail, setSendByEmail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Invoice history filters
  const [invEditionFilter, setInvEditionFilter] = useState('all');
  const [invClientFilter, setInvClientFilter] = useState('all');

  // Invoice history pagination
  const [invPage, setInvPage] = useState(1);
  // Invoice History rows opened with the [+] toggle (ids)
  const [expandedInvoices, setExpandedInvoices] = useState(() => new Set());
  const toggleInvoiceExpanded = (id) => setExpandedInvoices(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [invPageSize, setInvPageSize] = useState(25);

  // Searchable client combobox state
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Audit log modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyInvoice, setHistoryInvoice] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Exchange rate confirmation dialog (FX warning before generating invoices
  // when the current edition has no exchange rate defined)
  const [fxDialog, setFxDialog] = useState({
    open: false,
    defaultRate: null,
    editionName: '',
    onConfirm: null,
  });

  const checkExchangeRateThenRun = async (runFn) => {
    // A USD-only property has no exchange rate to warn about.
    if (!multiCurrency) { runFn(); return; }
    try {
      const res = await api.get('/editions/current');
      const ed = res?.edition;
      const editionRate = ed?.exchange_rate;
      // Only warn when the current edition has no/zero rate
      if (!editionRate || editionRate <= 0) {
        setFxDialog({
          open: true,
          defaultRate: res?.default_mxn_usd_exchange_rate ?? null,
          editionName: ed?.name || 'current edition',
          onConfirm: () => {
            setFxDialog((d) => ({ ...d, open: false, onConfirm: null }));
            runFn();
          },
        });
        return;
      }
    } catch (err) {
      // Non-fatal: fall back to normal flow if /editions/current fails
      console.warn('Could not check current edition exchange rate:', err);
    }
    runFn();
  };

  const openHistory = async (inv) => {
    setHistoryInvoice(inv);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryEntries([]);
    try {
      const data = await api.get(`/invoices/${inv.id}/audit-log`);
      setHistoryEntries(data || []);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Refresh-payment-link state (per-invoice)
  const [refreshingId, setRefreshingId] = useState(null);
  const handleRefreshPaymentLink = async (invoiceId) => {
    if (!window.confirm(
      'Stripe links expire after at most 30 days.\n\n' +
      'Generate a fresh payment link for this invoice? The old link will stop working immediately.'
    )) return;
    setRefreshingId(invoiceId);
    try {
      const res = await api.post(`/invoices/${invoiceId}/refresh-payment-link`, {
        origin_url: window.location.origin,
      });
      if (res.stripe_payment_url) {
        window.open(res.stripe_payment_url, '_blank', 'noopener');
        loadInitialData();
      }
    } catch (err) {
      alert('Failed to refresh link: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingId(null);
    }
  };

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    try {
      const [advData, edData, invData, delData] = await Promise.all([
        api.get('/advertisers'),
        api.get('/editions'),
        api.get('/invoices'),
        api.get('/invoices/deleted')
      ]);
      setAdvertisers(advData);
      setEditions(edData);
      setInvoices(invData);
      setDeletedInvoices(delData);
    } catch (err) { console.error('Failed to load data:', err); }
    finally { setLoading(false); }
  };

  const loadEditionBookings = async (edId) => {
    if (!edId) { setEditionBookings([]); setEditionSelectedIds([]); return; }
    try {
      const data = await api.get(`/invoices/uninvoiced-by-edition/${edId}`);
      setEditionBookings(data);
      setEditionSelectedIds(data.map(b => b.id));  // pre-select all by default
    } catch (err) { console.error('Failed to load edition bookings:', err); }
  };

  const handleEditionChange = (edId) => {
    setSelectedEdition(edId);
    loadEditionBookings(edId);
  };

  const toggleEditionBooking = (id) => {
    setEditionSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAllEditionBookings = () => {
    setEditionSelectedIds(prev => prev.length === editionBookings.length ? [] : editionBookings.map(b => b.id));
  };

  const handleBulkInvoiceByEdition = async () => {
    if (editionSelectedIds.length === 0 || !selectedEdition) return;
    const advCount = new Set(editionBookings.filter(b => editionSelectedIds.includes(b.id)).map(b => b.advertiser_id)).size;
    if (!window.confirm(`Create ${advCount} invoice${advCount !== 1 ? 's' : ''} for ${editionSelectedIds.length} booking${editionSelectedIds.length !== 1 ? 's' : ''}?${sendByEmail ? '\nEmails will be sent to each client.' : ''}`)) return;
    setBulkRunning(true);
    try {
      const res = await api.post('/invoices/bulk-by-edition', {
        edition_id: selectedEdition,
        booking_ids: editionSelectedIds,
        send_email: sendByEmail,
        origin_url: window.location.origin,
      });
      const msg = `Created ${res.count} invoice${res.count !== 1 ? 's' : ''}${res.failures.length > 0 ? `\n${res.failures.length} failure(s)` : ''}${sendByEmail ? '\nEmails dispatched.' : ''}`;
      alert(msg);
      // Refresh
      await loadEditionBookings(selectedEdition);
      const inv = await api.get('/invoices');
      setInvoices(inv);
    } catch (err) {
      alert('Bulk invoice failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkRunning(false);
    }
  };

  const loadUninvoiced = async (advId) => {
    if (!advId) { setUninvoicedBookings([]); return; }
    try {
      const data = await api.get(`/invoices/uninvoiced/${advId}`);
      setUninvoicedBookings(data);
      setSelectedBookingIds([]);
    } catch (err) { console.error('Failed to load uninvoiced bookings:', err); }
  };

  const handleAdvertiserChange = (advId) => {
    setSelectedAdvertiser(advId);
    loadUninvoiced(advId);
  };

  const toggleBooking = (id) => {
    setSelectedBookingIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelectedBookingIds(prev => prev.length === uninvoicedBookings.length ? [] : uninvoicedBookings.map(b => b.id));
  };

  const { subtotal, taxTotal, grandTotal } = useMemo(() => {
    const selected = uninvoicedBookings.filter(b => selectedBookingIds.includes(b.id));
    const sub = selected.reduce((s, b) => s + (b.rate || 0), 0);
    const tax = selected.reduce((s, b) => !b.is_taxable ? s : s + (b.rate || 0) * (b.tax_rate || 0) / 100, 0);
    return { subtotal: sub, taxTotal: tax, grandTotal: sub + tax };
  }, [uninvoicedBookings, selectedBookingIds]);

  // Filter invoices
  const filteredInvoices = useMemo(() => invoices.filter(inv => {
    const matchClient = invClientFilter === 'all' || inv.advertiser_id === invClientFilter;
    const matchEdition = invEditionFilter === 'all' || inv.line_items?.some(li => li.edition_name === invEditionFilter);
    return matchClient && matchEdition;
  }), [invoices, invClientFilter, invEditionFilter]);

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setInvPage(1); }, [invClientFilter, invEditionFilter, invPageSize]);

  // Slice for current page
  const totalInvPages = Math.max(1, Math.ceil(filteredInvoices.length / invPageSize));
  const pagedInvoices = useMemo(() => {
    const start = (invPage - 1) * invPageSize;
    return filteredInvoices.slice(start, start + invPageSize);
  }, [filteredInvoices, invPage, invPageSize]);

  // Unique edition names from invoices
  const invoiceEditions = useMemo(() => {
    const names = new Set();
    invoices.forEach(inv => inv.line_items?.forEach(li => { if (li.edition_name && li.edition_name !== 'N/A') names.add(li.edition_name); }));
    return [...names].sort();
  }, [invoices]);

  const handleCreateInvoice = async () => {
    if (selectedBookingIds.length === 0) return;
    setCreating(true);
    try {
      const inv = await api.post('/invoices', {
        advertiser_id: selectedAdvertiser, booking_ids: selectedBookingIds,
        send_email: sendByEmail, origin_url: window.location.origin
      });
      setInvoices(prev => [inv, ...prev]);
      setSelectedBookingIds([]);
      loadUninvoiced(selectedAdvertiser);
      alert(`Invoice ${inv.invoice_number} created!${sendByEmail ? ' Email sent.' : ''}`);
    } catch (err) { alert('Failed to create invoice.'); }
    finally { setCreating(false); }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('PDF download failed. Please try again.');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    try {
      const result = await api.post(`/invoices/${invoiceId}/send-email`);
      alert(result.message || 'Email sent!');
      setInvoices(await api.get('/invoices'));
    } catch (err) { alert('Failed to send email.'); }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      const updated = await api.put(`/invoices/${invoiceId}/status`, { status: newStatus });
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? updated : inv));
    } catch (err) { console.error('Status update failed:', err); }
  };

  const handleDeleteClick = (invoiceId, invoiceNumber) => {
    setDeleteTarget({ id: invoiceId, number: invoiceNumber });
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/invoices/${deleteTarget.id}`, {
        data: { reason: deleteReason.trim() },
        withCredentials: true
      });
      const [invData, delData] = await Promise.all([api.get('/invoices'), api.get('/invoices/deleted')]);
      setInvoices(invData); setDeletedInvoices(delData);
      if (selectedAdvertiser) loadUninvoiced(selectedAdvertiser);
      setDeleteDialogOpen(false);
    } catch (err) { alert('Failed to delete invoice.'); }
    finally { setDeleting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
          <Receipt className="w-5 h-5 text-[#3B82F6]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Accounting</h1>
          <p className="text-[#94A3B8] mt-1">Create and manage invoices</p>
        </div>
      </div>

      {/* Invoice Creation */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md">
        <div className="p-4 border-b border-[#1E293B] flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-[#F8FAFC]">Create Invoice</h2>
          <div className="inline-flex bg-[#0A0A0A] border border-[#1E293B] rounded-md p-1" data-testid="create-mode-toggle">
            <button
              onClick={() => setCreateMode('client')}
              className={`px-3 py-1 text-xs rounded ${createMode === 'client' ? 'bg-[#3B82F6] text-white' : 'text-[#94A3B8] hover:text-[#F8FAFC]'}`}
              data-testid="create-mode-client"
            >By Client</button>
            <button
              onClick={() => setCreateMode('edition')}
              className={`px-3 py-1 text-xs rounded ${createMode === 'edition' ? 'bg-[#3B82F6] text-white' : 'text-[#94A3B8] hover:text-[#F8FAFC]'}`}
              data-testid="create-mode-edition"
            >By Edition (Mass)</button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {createMode === 'client' ? (
            <>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Select Client</Label>
                <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientPopoverOpen}
                      className="w-full max-w-md justify-between bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] hover:bg-[#1A1A1A]"
                      data-testid="invoice-client-select"
                    >
                      {selectedAdvertiser
                        ? advertisers.find(a => a.id === selectedAdvertiser)?.business_name || 'Choose a client to invoice'
                        : 'Choose a client to invoice'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 bg-[#121212] border-[#1E293B]" align="start">
                    <Command className="bg-[#121212]">
                      <CommandInput
                        placeholder="Type to filter clients..."
                        className="text-[#F8FAFC]"
                        data-testid="invoice-client-search-input"
                      />
                      <CommandList>
                        <CommandEmpty className="text-[#94A3B8] text-sm py-4 text-center">No client found.</CommandEmpty>
                        <CommandGroup>
                          {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                            <CommandItem
                              key={adv.id}
                              value={adv.business_name}
                              onSelect={() => { handleAdvertiserChange(adv.id); setClientPopoverOpen(false); }}
                              className="text-[#F8FAFC] cursor-pointer"
                              data-testid={`invoice-client-option-${adv.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${selectedAdvertiser === adv.id ? 'opacity-100 text-[#3B82F6]' : 'opacity-0'}`} />
                              {adv.business_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedAdvertiser && (
                <>
                  <UninvoicedBookingsTable bookings={uninvoicedBookings} selectedIds={selectedBookingIds} onToggle={toggleBooking} onToggleAll={toggleAll} />
                  {selectedBookingIds.length > 0 && (
                    <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1E293B] rounded-md">
                      <div className="flex items-center gap-6">
                        <div><p className="text-xs text-[#94A3B8]">Subtotal</p><p className="text-lg font-medium text-[#F8FAFC]">{formatCurrency(subtotal)}</p></div>
                        <div><p className="text-xs text-[#94A3B8]">Tax</p><p className="text-lg font-medium text-[#F8FAFC]">{formatCurrency(taxTotal)}</p></div>
                        <div><p className="text-xs text-[#94A3B8]">Grand Total</p><p className="text-xl font-bold text-[#3B82F6]">{formatCurrency(grandTotal)}</p></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch checked={sendByEmail} onCheckedChange={setSendByEmail} data-testid="send-email-switch" />
                          <Label className="text-[#94A3B8] text-sm">Send by email</Label>
                        </div>
                        <Button onClick={() => checkExchangeRateThenRun(handleCreateInvoice)} disabled={creating} data-testid="create-invoice-btn" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                          {creating ? 'Creating...' : (<><Receipt className="w-4 h-4 mr-2" /> Create Invoice ({selectedBookingIds.length} items)</>)}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Select Edition</Label>
                <Select value={selectedEdition} onValueChange={handleEditionChange}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="invoice-edition-select">
                    <SelectValue placeholder="Choose an edition to mass-invoice" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B]">
                    {[...editions]
                      .sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || ''))
                      .map(ed => (
                        <SelectItem key={ed.id} value={ed.id} className="text-[#F8FAFC]">
                          {ed.name} — {ed.publish_date}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#475569]">All confirmed/production bookings in this edition that haven't been invoiced yet will be listed below. One invoice will be created per client.</p>
              </div>

              {selectedEdition && (
                <EditionBulkBookingsTable
                  bookings={editionBookings}
                  selectedIds={editionSelectedIds}
                  onToggle={toggleEditionBooking}
                  onToggleAll={toggleAllEditionBookings}
                  advertisersById={Object.fromEntries(advertisers.map(a => [a.id, a]))}
                />
              )}

              {selectedEdition && editionSelectedIds.length > 0 && (() => {
                // Find clients without a delivery email
                const selectedBookings = editionBookings.filter(b => editionSelectedIds.includes(b.id));
                const uniqueAdvIds = [...new Set(selectedBookings.map(b => b.advertiser_id))];
                const advsById = Object.fromEntries(advertisers.map(a => [a.id, a]));
                const clientsNoEmail = uniqueAdvIds
                  .map(id => advsById[id])
                  .filter(adv => adv && !(adv.email || adv.contact_email));
                return (
                <div className="space-y-3" data-testid="bulk-invoice-summary">
                  {sendByEmail && clientsNoEmail.length > 0 && (
                    <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-md flex items-start gap-3" data-testid="bulk-no-email-warning">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-300 flex-1">
                        <p className="font-medium">
                          {clientsNoEmail.length} client{clientsNoEmail.length !== 1 ? 's' : ''} {clientsNoEmail.length === 1 ? 'has' : 'have'} no email — invoice{clientsNoEmail.length !== 1 ? 's' : ''} will still be created but not delivered:
                        </p>
                        <p className="text-xs text-red-200/80 mt-1">
                          {clientsNoEmail.map(a => a.business_name).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1E293B] rounded-md flex-wrap gap-3">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <p className="text-xs text-[#94A3B8]">Selected Bookings</p>
                      <p className="text-lg font-medium text-[#F8FAFC]">{editionSelectedIds.length}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Unique Clients</p>
                      <p className="text-lg font-medium text-[#F8FAFC]">
                        {uniqueAdvIds.length}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Total Revenue</p>
                      <p className="text-xl font-bold text-[#3B82F6]">
                        {formatCurrency(selectedBookings.reduce((s, b) => s + (b.rate || 0), 0))}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={sendByEmail} onCheckedChange={setSendByEmail} data-testid="bulk-send-email-switch" />
                      <Label className="text-[#94A3B8] text-sm">Send by email</Label>
                    </div>
                    <Button
                      onClick={() => checkExchangeRateThenRun(handleBulkInvoiceByEdition)}
                      disabled={bulkRunning}
                      data-testid="bulk-create-invoices-btn"
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                    >
                      {bulkRunning ? 'Creating…' : (<><Receipt className="w-4 h-4 mr-2" /> Create Invoices ({uniqueAdvIds.length})</>)}
                    </Button>
                  </div>
                </div>
                </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Invoice History */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md">
        <div className="p-4 border-b border-[#1E293B] flex flex-col md:flex-row md:items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-[#F8FAFC]">Invoice History</h2>
          <div className="flex gap-3">
            <Select value={invClientFilter} onValueChange={setInvClientFilter}>
              <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm" data-testid="inv-client-filter">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All Clients</SelectItem>
                {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                  <SelectItem key={adv.id} value={adv.id} className="text-[#F8FAFC]">{adv.business_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={invEditionFilter} onValueChange={setInvEditionFilter}>
              <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm" data-testid="inv-edition-filter">
                <SelectValue placeholder="All Editions" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All Editions</SelectItem>
                {invoiceEditions.map(name => (
                  <SelectItem key={name} value={name} className="text-[#F8FAFC]">{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {filteredInvoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
                  <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Invoice #</th>
                  <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Client</th>
                  <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Date</th>
                  <th className="py-3 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Total</th>
                  <th className="py-3 px-4 text-center text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Status</th>
                  <th className="py-3 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {pagedInvoices.map(inv => {
                  const expanded = expandedInvoices.has(inv.id);
                  return (
                  <React.Fragment key={inv.id}>
                  <tr className={`hover:bg-[#1A1A1A] ${expanded ? 'bg-[#1A1A1A]' : ''}`} data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-3 px-4 text-sm font-mono text-[#3B82F6]">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleInvoiceExpanded(inv.id)}
                          className="text-[#94A3B8] hover:text-[#F8FAFC] shrink-0"
                          title={expanded ? 'Hide invoice details' : 'Show what this invoice bills'}
                          aria-expanded={expanded}
                          aria-label={expanded ? `Hide details of ${inv.invoice_number}` : `Show details of ${inv.invoice_number}`}
                          data-testid={`invoice-expand-${inv.id}`}
                        >
                          {expanded ? <SquareMinus className="w-4 h-4" /> : <SquarePlus className="w-4 h-4" />}
                        </button>
                        <span className="whitespace-nowrap">{inv.invoice_number}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#F8FAFC]">{inv.advertiser_name}</td>
                    <td className="py-3 px-4 text-sm text-[#94A3B8]">{formatDate(inv.created_at)}</td>
                    <td className="py-3 px-4 text-sm font-medium text-[#F8FAFC] text-right">{formatCurrency(inv.total)}</td>
                    <td className="py-3 px-4 text-center">
                      <Select value={inv.status} onValueChange={(val) => handleStatusChange(inv.id, val)}>
                        <SelectTrigger className={`h-7 w-auto min-w-[100px] border-0 bg-transparent px-2 py-0 text-xs rounded-full focus:ring-0 focus:ring-offset-0 ${invoiceStatusColors[inv.status] || invoiceStatusColors.draft}`} data-testid={`inv-status-${inv.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#1E293B]">
                          {invoiceStatusOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-[#F8FAFC] text-xs">{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openHistory(inv)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" title="View history" data-testid={`invoice-history-${inv.id}`}><History className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(inv.id, inv.invoice_number)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" title="Download PDF" data-testid={`download-pdf-${inv.id}`}><Download className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSendEmail(inv.id)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" title="Send email" data-testid={`send-email-${inv.id}`}><Mail className="w-4 h-4" /></Button>
                        {inv.stripe_payment_url && (
                          <a href={inv.stripe_payment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 text-[#94A3B8] hover:text-[#3B82F6]" title={multiCurrency ? 'Payment link (USD)' : 'Payment link'} data-testid={`payment-link-${inv.id}`}><ExternalLink className="w-4 h-4" /></a>
                        )}
                        {mxnPayUrl(inv, multiCurrency) && (
                          <a href={mxnPayUrl(inv, multiCurrency)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 px-1 text-[10px] font-semibold text-[#94A3B8] hover:text-[#3B82F6]" title="Payment link (MXN)" data-testid={`payment-link-mxn-${inv.id}`}>MXN</a>
                        )}
                        {inv.status !== 'paid' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRefreshPaymentLink(inv.id)}
                            disabled={refreshingId === inv.id}
                            className="text-[#94A3B8] hover:text-[#3B82F6] h-8 w-8 p-0"
                            title="Refresh Stripe payment link (if old link expired)"
                            data-testid={`refresh-payment-link-${inv.id}`}
                          >
                            <RefreshCw className={`w-4 h-4 ${refreshingId === inv.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(inv.id, inv.invoice_number)} className="text-[#94A3B8] hover:text-red-400 h-8 w-8 p-0" title="Delete" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                  {expanded && <InvoiceDetailsRow invoice={inv} colSpan={6} multiCurrency={multiCurrency} />}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {/* Pagination footer */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 border-t border-[#1E293B] text-sm" data-testid="invoice-pagination">
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <span>Rows per page:</span>
                <Select value={String(invPageSize)} onValueChange={(v) => setInvPageSize(Number(v))}>
                  <SelectTrigger className="w-[80px] bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-8 text-sm" data-testid="invoice-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B]">
                    {[10, 25, 50, 100].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-[#F8FAFC]">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="ml-3">
                  {filteredInvoices.length === 0
                    ? '0 of 0'
                    : `${(invPage - 1) * invPageSize + 1}-${Math.min(invPage * invPageSize, filteredInvoices.length)} of ${filteredInvoices.length}`}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setInvPage(1)}
                  disabled={invPage === 1}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 px-2"
                  data-testid="invoice-page-first"
                >« First</Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setInvPage(p => Math.max(1, p - 1))}
                  disabled={invPage === 1}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 px-2"
                  data-testid="invoice-page-prev"
                >‹ Prev</Button>
                <span className="text-[#F8FAFC] px-3" data-testid="invoice-page-indicator">
                  Page {invPage} of {totalInvPages}
                </span>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setInvPage(p => Math.min(totalInvPages, p + 1))}
                  disabled={invPage >= totalInvPages}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 px-2"
                  data-testid="invoice-page-next"
                >Next ›</Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setInvPage(totalInvPages)}
                  disabled={invPage >= totalInvPages}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 px-2"
                  data-testid="invoice-page-last"
                >Last »</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-[#94A3B8]">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-lg">No invoices match your filters</p>
          </div>
        )}
      </div>

      {/* Create Receipt (accordion) */}
      <CreateReceiptSection advertisers={advertisers} invoices={invoices} />

      {/* Deleted Invoices */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md">
        <button onClick={() => setShowDeletedSection(!showDeletedSection)} className="w-full p-4 flex items-center justify-between hover:bg-[#1A1A1A] transition-colors" data-testid="toggle-deleted-invoices">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-semibold text-[#F8FAFC]">Deleted Invoices {deletedInvoices.length > 0 && <span className="text-sm font-normal text-[#94A3B8]">({deletedInvoices.length})</span>}</h2>
          </div>
          {showDeletedSection ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
        </button>
        {showDeletedSection && (
          deletedInvoices.length > 0 ? (
            <div className="overflow-x-auto border-t border-[#1E293B]">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
                    <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Invoice #</th>
                    <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Client</th>
                    <th className="py-3 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Total</th>
                    <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Deleted By</th>
                    <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Date</th>
                    <th className="py-3 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Reason</th>
                    <th className="py-3 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">History</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {deletedInvoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-[#1A1A1A]" data-testid={`deleted-invoice-${inv.id}`}>
                      <td className="py-3 px-4 text-sm font-mono text-red-400 line-through">{inv.invoice_number}</td>
                      <td className="py-3 px-4 text-sm text-[#94A3B8]">{inv.advertiser_name}</td>
                      <td className="py-3 px-4 text-sm text-[#94A3B8] text-right">{formatCurrency(inv.total)}</td>
                      <td className="py-3 px-4 text-sm text-[#94A3B8]">{inv.deleted_by}</td>
                      <td className="py-3 px-4 text-sm text-[#94A3B8]">{formatDate(inv.deleted_at)}</td>
                      <td className="py-3 px-4 text-sm text-[#94A3B8] max-w-xs truncate">{inv.deletion_reason}</td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openHistory(inv)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0" title="View history" data-testid={`deleted-invoice-history-${inv.id}`}><History className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-[#94A3B8] border-t border-[#1E293B]">
              <Archive className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No deleted invoices</p>
            </div>
          )
        )}
      </div>

      {/* Facturas Client List */}
      <FacturasClientList editions={editions} />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" /> Delete Invoice {deleteTarget?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">This will delete the invoice and release all associated bookings so they can be reinvoiced.</p>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Reason for deletion *</Label>
              <Textarea data-testid="delete-reason-input" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="e.g., Duplicate invoice, wrong client..." className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] min-h-[80px]" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
              <Button onClick={handleConfirmDelete} disabled={!deleteReason.trim() || deleting} data-testid="confirm-delete-invoice-btn" className="bg-red-600 hover:bg-red-700 text-white">
                {deleting ? 'Deleting...' : 'Delete Invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log / History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="invoice-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#3B82F6]" />
              History — Invoice {historyInvoice?.invoice_number}
              {historyInvoice?.is_deleted && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/50 text-red-400 border border-red-800">DELETED</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {historyLoading ? (
              <div className="py-8 text-center text-[#94A3B8]">Loading…</div>
            ) : historyEntries.length === 0 ? (
              <div className="py-8 text-center text-[#94A3B8]" data-testid="invoice-history-empty">
                No log entries recorded for this invoice yet.
              </div>
            ) : (
              <ol className="relative border-l border-[#1E293B] ml-3 space-y-4 pt-1">
                {historyEntries.map((e, i) => (
                  <li key={e.id || i} className="ml-4" data-testid={`invoice-history-entry-${i}`}>
                    <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-[#3B82F6] border-2 border-[#121212]" />
                    <div className="text-xs font-mono text-[#94A3B8]">
                      {new Date(e.timestamp).toLocaleString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </div>
                    <div className="text-sm text-[#F8FAFC] mt-0.5">
                      {e.message}
                      {e.actor_name && (
                        <span className="text-[#94A3B8]">
                          {' '}— by {e.actor_name}
                          {e.actor_email && <span className="text-[#475569]"> ({e.actor_email})</span>}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Exchange Rate confirmation dialog */}
      <Dialog
        open={fxDialog.open}
        onOpenChange={(open) =>
          setFxDialog((d) => ({ ...d, open, onConfirm: open ? d.onConfirm : null }))
        }
      >
        <DialogContent
          className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md"
          data-testid="fx-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#F8FAFC]">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              No Exchange Rate Defined
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#94A3B8]">
              You don't have an exchange rate defined for{' '}
              <span className="text-[#F8FAFC] font-medium">{fxDialog.editionName}</span>.
            </p>
            <p className="text-[#94A3B8]">
              Are you sure you want to generate the invoice(s) with the default MXN/USD
              exchange rate
              {fxDialog.defaultRate
                ? (
                  <span className="text-orange-300 font-medium"> ({fxDialog.defaultRate})</span>
                )
                : (
                  <span className="text-red-400"> (none configured — defaults to 1.0)</span>
                )}
              ?
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFxDialog({ open: false, defaultRate: null, editionName: '', onConfirm: null })}
              className="border-[#1E293B] text-[#94A3B8]"
              data-testid="fx-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => fxDialog.onConfirm && fxDialog.onConfirm()}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              data-testid="fx-confirm-proceed"
            >
              Yes, Use Default Rate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Facturas Client List
const FacturasClientList = ({ editions }) => {
  const { multiCurrency } = useFeatures();
  // USD-only property: every row is USD and there is no conversion.
  const defaultCcy = multiCurrency ? 'MXN' : 'USD';
  const [open, setOpen] = useState(false);
  const [edId, setEdId] = useState('');
  const [data, setData] = useState(null);
  const [currencies, setCurrencies] = useState({});  // booking_id → "MXN" | "USD"
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'xlsx' | 'csv' | 'pdf' | null

  const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const loadEdition = async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/facturas/clients-by-edition/${id}`);
      setData(res);
      const map = {};
      (res.rows || []).forEach(r => { map[r.booking_id] = defaultCcy; });
      setCurrencies(map);
    } catch (err) {
      alert('Failed to load: ' + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  };

  const setRowCurrency = (bid, ccy) => setCurrencies(prev => ({ ...prev, [bid]: ccy }));

  const exchangeRate = data?.edition?.exchange_rate || 0;

  const computeAmount = (row) => {
    const ccy = currencies[row.booking_id] || defaultCcy;
    if (ccy === 'MXN' && exchangeRate > 0) {
      return {
        currency: 'MXN',
        rate: row.rate * exchangeRate,
        tax: row.tax_amount * exchangeRate,
        total: row.total * exchangeRate,
      };
    }
    return { currency: 'USD', rate: row.rate, tax: row.tax_amount, total: row.total };
  };

  // Group rows by advertiser for per-client totals
  const grouped = useMemo(() => {
    const out = [];
    if (!data?.rows) return out;
    let buf = [];
    let advId = null;
    const flush = () => {
      if (!buf.length) return;
      const sums = { MXN: 0, USD: 0 };
      buf.forEach(r => { const a = computeAmount(r); sums[a.currency] += a.total; });
      out.push({ kind: 'total', advertiser_name: buf[0].advertiser_name || '(Unknown)', sums, count: buf.length });
    };
    for (const r of data.rows) {
      if (r.advertiser_id !== advId && buf.length) { flush(); buf = []; }
      out.push({ kind: 'data', row: r });
      buf.push(r);
      advId = r.advertiser_id;
    }
    flush();
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currencies, exchangeRate]);

  const totals = grouped.reduce((acc, g) => {
    if (g.kind === 'total') {
      acc.mxn += g.sums.MXN;
      acc.usd += g.sums.USD;
    }
    return acc;
  }, { mxn: 0, usd: 0 });

  const downloadFile = async (format) => {
    if (!edId) return;
    setDownloading(format);
    try {
      const rows = (data?.rows || []).map(r => ({
        booking_id: r.booking_id,
        currency: currencies[r.booking_id] || defaultCcy,
      }));
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/facturas/clients-by-edition/${edId}/export`,
        { rows, format },
        {
          responseType: 'blob',
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (data?.edition?.name || edId).replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '_');
      a.download = `facturas_${safe}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDownloading(null); }
  };

  return (
    <div className="bg-[#121212] border border-[#1E293B] rounded-md" data-testid="facturas-client-list-section">
      <button onClick={() => setOpen(o => !o)} className="w-full p-4 flex items-center justify-between hover:bg-[#1A1A1A] transition-colors" data-testid="toggle-facturas-list">
        <div className="flex items-center gap-3">
          <Receipt className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-[#F8FAFC]">Facturas Client List</h2>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
      </button>

      {open && (
        <div className="border-t border-[#1E293B] p-4 space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-2 flex-1 min-w-[260px] max-w-md">
              <Label className="text-[#94A3B8]">Edition</Label>
              <Select value={edId} onValueChange={(v) => { setEdId(v); loadEdition(v); }}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="facturas-edition-select">
                  <SelectValue placeholder="Choose an edition…" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {[...editions].sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || '')).map(ed => (
                    <SelectItem key={ed.id} value={ed.id} className="text-[#F8FAFC]">
                      {ed.name} — {ed.publish_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {data && (data?.rows?.length || 0) > 0 && (
              <div className="flex gap-2">
                <Button onClick={() => downloadFile('xlsx')} disabled={!!downloading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="facturas-export-xlsx">
                  <Download className="w-4 h-4 mr-2" />{downloading === 'xlsx' ? '…' : 'Excel'}
                </Button>
                <Button onClick={() => downloadFile('csv')} disabled={!!downloading} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="facturas-export-csv">
                  <Download className="w-4 h-4 mr-2" />{downloading === 'csv' ? '…' : 'CSV'}
                </Button>
                <Button onClick={() => downloadFile('pdf')} disabled={!!downloading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="facturas-export-pdf">
                  <Download className="w-4 h-4 mr-2" />{downloading === 'pdf' ? '…' : 'PDF'}
                </Button>
              </div>
            )}
          </div>

          {data && (
            <div className="text-xs text-[#94A3B8] flex gap-4 flex-wrap" data-testid="facturas-meta">
              <span>Edition: <strong className="text-[#F8FAFC]">{data.edition.name}</strong></span>
              {multiCurrency && (
                <span>Exchange rate: <strong className="text-[#F8FAFC]">{exchangeRate ? exchangeRate.toFixed(4) : '(not set on edition)'}</strong></span>
              )}
              <span>Rows: <strong className="text-[#F8FAFC]">{data.rows.length}</strong></span>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-[#475569] text-center py-8">Loading…</p>
          ) : data && data.rows.length === 0 ? (
            <p className="text-sm text-[#475569] text-center py-8">No clients need a factura for this edition.</p>
          ) : data ? (
            <div className="overflow-x-auto border border-[#1E293B] rounded-md">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
                    <th className="py-2 px-3 text-left text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Client</th>
                    <th className="py-2 px-3 text-left text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Product</th>
                    <th className="py-2 px-3 text-left text-[10px] font-medium tracking-wide uppercase text-[#94A3B8] w-28">Currency</th>
                    <th className="py-2 px-3 text-right text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Rate</th>
                    <th className="py-2 px-3 text-right text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Tax %</th>
                    <th className="py-2 px-3 text-right text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Tax $</th>
                    <th className="py-2 px-3 text-right text-[10px] font-medium tracking-wide uppercase text-[#94A3B8]">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {grouped.map((g, idx) => {
                    if (g.kind === 'data') {
                      const r = g.row;
                      const a = computeAmount(r);
                      const ccy = currencies[r.booking_id] || defaultCcy;
                      return (
                        <tr key={r.booking_id} className="hover:bg-[#1A1A1A]" data-testid={`facturas-row-${r.booking_id}`}>
                          <td className="py-1.5 px-3 text-sm text-[#F8FAFC]">{r.advertiser_name}</td>
                          <td className="py-1.5 px-3 text-sm text-[#94A3B8]">{r.product_type}</td>
                          <td className="py-1.5 px-3">
                            {!multiCurrency ? (
                              <span className="text-xs text-[#94A3B8]">USD</span>
                            ) : (
                            <select
                              value={ccy}
                              onChange={(e) => setRowCurrency(r.booking_id, e.target.value)}
                              disabled={ccy === 'MXN' && exchangeRate <= 0}
                              className="bg-[#0A0A0A] border border-[#1E293B] text-[#F8FAFC] text-xs rounded h-7 px-2 w-24"
                              data-testid={`facturas-ccy-${r.booking_id}`}
                            >
                              <option value="MXN" disabled={exchangeRate <= 0}>MXN</option>
                              <option value="USD">USD</option>
                            </select>
                            )}
                          </td>
                          <td className="py-1.5 px-3 text-sm text-[#F8FAFC] text-right">${fmtMoney(a.rate)}</td>
                          <td className="py-1.5 px-3 text-sm text-[#94A3B8] text-right">{r.tax_rate}%</td>
                          <td className="py-1.5 px-3 text-sm text-[#94A3B8] text-right">${fmtMoney(a.tax)}</td>
                          <td className="py-1.5 px-3 text-sm text-[#F8FAFC] text-right font-medium">
                            ${fmtMoney(a.total)} <span className="text-[10px] text-[#475569]">{a.currency}</span>
                          </td>
                        </tr>
                      );
                    }
                    // group total row
                    const parts = [];
                    if (g.sums.MXN > 0) parts.push(`MXN: $${fmtMoney(g.sums.MXN)}`);
                    if (g.sums.USD > 0) parts.push(`USD: $${fmtMoney(g.sums.USD)}`);
                    return g.count > 1 ? (
                      <tr key={`total-${idx}`} className="bg-[#0F172A]" data-testid={`facturas-group-total-${g.advertiser_name}`}>
                        <td colSpan={6} className="py-1.5 px-3 text-xs font-bold uppercase tracking-wide text-[#3B82F6] text-right">
                          Total for {g.advertiser_name}
                        </td>
                        <td className="py-1.5 px-3 text-sm text-right font-bold text-[#F8FAFC]">{parts.join(' | ')}</td>
                      </tr>
                    ) : null;
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0F172A] border-t-2 border-[#3B82F6]/30">
                    <td colSpan={6} className="py-2 px-3 text-xs font-bold text-[#3B82F6] uppercase text-right">Grand Total</td>
                    <td className="py-2 px-3 text-sm text-right">
                      {totals.mxn > 0 && <p className="font-semibold text-[#F8FAFC]">${fmtMoney(totals.mxn)} <span className="text-[10px] text-[#475569]">MXN</span></p>}
                      {totals.usd > 0 && <p className="font-semibold text-[#F8FAFC]">${fmtMoney(totals.usd)} <span className="text-[10px] text-[#475569]">USD</span></p>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}

          {multiCurrency && data && exchangeRate <= 0 && (
            <p className="text-xs text-amber-400">⚠ This edition has no exchange rate set. Set one on the edition to enable MXN conversion.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create Receipt Section (accordion – closed by default)
// ---------------------------------------------------------------------------
const CreateReceiptSection = ({ advertisers, invoices }) => {
  const { multiCurrency } = useFeatures();
  const [open, setOpen] = useState(false);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [amountPaid, setAmountPaid] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [sendByEmail, setSendByEmail] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Receipt history
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  const loadReceipts = async () => {
    setReceiptsLoading(true);
    try {
      const data = await api.get('/receipts');
      setReceipts(data || []);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    } finally {
      setReceiptsLoading(false);
    }
  };

  useEffect(() => { if (open) loadReceipts(); }, [open]);

  // Reset invoice selection whenever advertiser changes
  useEffect(() => {
    setSelectedInvoiceIds([]);
    setAmountPaid('');
  }, [selectedAdvertiser]);

  const clientInvoices = useMemo(() => {
    if (!selectedAdvertiser) return [];
    return invoices
      .filter(inv => inv.advertiser_id === selectedAdvertiser && inv.status !== 'cancelled')
      .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  }, [invoices, selectedAdvertiser]);

  // Convert to receipt currency for display.
  // If user typed a custom rate → use it for every invoice.
  // Otherwise → fall back to each invoice's own exchange_rate (set by its edition).
  const customRate = parseFloat(exchangeRate) || 0;
  const { totalInvoicedInCurrency, missingRates } = useMemo(() => {
    if (currency !== 'MXN') {
      const total = clientInvoices
        .filter(i => selectedInvoiceIds.includes(i.id))
        .reduce((s, i) => s + (i.total || 0), 0);
      return { totalInvoicedInCurrency: total, missingRates: [] };
    }
    let total = 0;
    const missing = [];
    clientInvoices.filter(i => selectedInvoiceIds.includes(i.id)).forEach(i => {
      const r = customRate > 0 ? customRate : (i.exchange_rate || 0);
      if (r > 0) {
        total += (i.total || 0) * r;
      } else {
        missing.push(i.invoice_number);
      }
    });
    return { totalInvoicedInCurrency: total, missingRates: missing };
  }, [clientInvoices, selectedInvoiceIds, currency, customRate]);

  const amountPaidNum = parseFloat(amountPaid) || 0;
  const balance = Math.max(0, totalInvoicedInCurrency - amountPaidNum);

  const currencySymbol = currency === 'MXN' ? 'MX$' : '$';
  const formatMoney = (v) => `${currencySymbol}${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const toggleInvoice = (id) => {
    setSelectedInvoiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const canSubmit = selectedAdvertiser && selectedInvoiceIds.length > 0 && amountPaidNum > 0 &&
                    (currency === 'USD' || missingRates.length === 0);

  const handleGenerate = async (download = true) => {
    if (!canSubmit) return;
    setGenerating(true);
    try {
      const payload = {
        advertiser_id: selectedAdvertiser,
        invoice_ids: selectedInvoiceIds,
        amount_paid: amountPaidNum,
        currency,
        // Only send custom rate when user typed one; otherwise backend falls
        // back to each invoice's own exchange_rate.
        exchange_rate: currency === 'MXN' && customRate > 0 ? customRate : null,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        notes,
        send_email: sendByEmail,
      };
      const receipt = await api.post('/receipts', payload);
      setReceipts(prev => [receipt, ...prev]);

      if (download) {
        // Trigger PDF download
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/receipts/${receipt.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${receipt.receipt_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      // Reset form
      setSelectedInvoiceIds([]);
      setAmountPaid('');
      setNotes('');
      setSendByEmail(false);
      if (receipt.email_warning) {
        alert(`Receipt ${receipt.receipt_number} was created and downloaded, but the email could not be sent: ${receipt.email_warning}`);
      } else if (sendByEmail) {
        alert(`Receipt ${receipt.receipt_number} created and emailed successfully.`);
      } else {
        alert(`Receipt ${receipt.receipt_number} created successfully.`);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to create receipt: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadReceipt = async (rc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/receipts/${rc.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rc.receipt_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download receipt: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSendReceiptEmail = async (rc) => {
    if (!window.confirm(`Send receipt ${rc.receipt_number} to ${rc.advertiser_email || 'the client'}?`)) return;
    try {
      await api.post(`/receipts/${rc.id}/send-email`, {});
      alert(`Receipt ${rc.receipt_number} sent.`);
      loadReceipts();
    } catch (err) {
      alert('Failed to send receipt: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteReceipt = async (rc) => {
    if (!window.confirm(`Delete receipt ${rc.receipt_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/receipts/${rc.id}`);
      setReceipts(prev => prev.filter(r => r.id !== rc.id));
    } catch (err) {
      alert('Failed to delete receipt: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="bg-[#121212] border border-[#1E293B] rounded-md" data-testid="create-receipt-section">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-4 flex items-center justify-between hover:bg-[#1A1A1A] transition-colors"
        data-testid="create-receipt-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <FileCheck className="w-5 h-5 text-emerald-400" />
          <div className="text-left">
            <h2 className="text-lg font-semibold text-[#F8FAFC]">Create Receipt</h2>
            <p className="text-xs text-[#94A3B8]">Generate a downloadable payment receipt for invoices paid by cash, bank transfer, etc.</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
      </button>

      {open && (
        <div className="border-t border-[#1E293B] p-4 space-y-5">
          {/* Client selector */}
          <div>
            <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Client</Label>
            <Select value={selectedAdvertiser} onValueChange={setSelectedAdvertiser}>
              <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm" data-testid="receipt-client-select">
                <SelectValue placeholder="Choose a client…" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                  <SelectItem key={adv.id} value={adv.id} className="text-[#F8FAFC]">{adv.business_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedAdvertiser && (
            <>
              {/* Invoices multi-select */}
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Invoices to include</Label>
                {clientInvoices.length === 0 ? (
                  <p className="mt-2 text-sm text-[#94A3B8] italic">This client has no invoices.</p>
                ) : (
                  <div className="mt-1 border border-[#1E293B] rounded-md max-h-[240px] overflow-y-auto" data-testid="receipt-invoice-list">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#0A0A0A] border-b border-[#1E293B]">
                          <th className="py-2 px-3 text-left w-10"></th>
                          <th className="py-2 px-3 text-left text-xs uppercase text-[#94A3B8]">Invoice #</th>
                          <th className="py-2 px-3 text-left text-xs uppercase text-[#94A3B8]">Date</th>
                          <th className="py-2 px-3 text-right text-xs uppercase text-[#94A3B8]">Total (USD)</th>
                          <th className="py-2 px-3 text-center text-xs uppercase text-[#94A3B8]">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#1E293B]">
                        {clientInvoices.map(inv => (
                          <tr key={inv.id} className="hover:bg-[#1A1A1A]" data-testid={`receipt-invoice-row-${inv.id}`}>
                            <td className="py-2 px-3">
                              <Checkbox
                                checked={selectedInvoiceIds.includes(inv.id)}
                                onCheckedChange={() => toggleInvoice(inv.id)}
                                data-testid={`receipt-invoice-checkbox-${inv.id}`}
                              />
                            </td>
                            <td className="py-2 px-3 font-mono text-[#3B82F6]">{inv.invoice_number}</td>
                            <td className="py-2 px-3 text-[#94A3B8]">{formatDate(inv.created_at)}</td>
                            <td className="py-2 px-3 text-right text-[#F8FAFC]">${(inv.total || 0).toFixed(2)}</td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${invoiceStatusColors[inv.status] || invoiceStatusColors.draft}`}>{inv.status || 'draft'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Payment details form */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {multiCurrency && (
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm" data-testid="receipt-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#1E293B]">
                      <SelectItem value="USD" className="text-[#F8FAFC]">USD ($)</SelectItem>
                      <SelectItem value="MXN" className="text-[#F8FAFC]">MXN (MX$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}

                {currency === 'MXN' && (
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Custom Exchange rate (USD → MXN)</Label>
                    <Input
                      type="number" step="0.0001" min="0"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      placeholder="Leave blank to use each invoice&rsquo;s own rate"
                      className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm"
                      data-testid="receipt-exchange-rate-input"
                    />
                    <p className="mt-1 text-[11px] text-[#64748B]" data-testid="receipt-exchange-rate-helper">
                      Blank = each invoice keeps the exchange rate defined by its edition.
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Amount paid</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm"
                    data-testid="receipt-amount-paid-input"
                  />
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Payment method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm" data-testid="receipt-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#1E293B]">
                      <SelectItem value="cash" className="text-[#F8FAFC]">Cash</SelectItem>
                      <SelectItem value="bank_transfer" className="text-[#F8FAFC]">Bank Transfer</SelectItem>
                      <SelectItem value="check" className="text-[#F8FAFC]">Check</SelectItem>
                      <SelectItem value="credit_card" className="text-[#F8FAFC]">Credit Card</SelectItem>
                      <SelectItem value="other" className="text-[#F8FAFC]">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Payment date</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm"
                    data-testid="receipt-payment-date-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wide">Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Reference number, bank details, or any other note to appear on the receipt…"
                  className="mt-1 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] text-sm"
                  data-testid="receipt-notes-input"
                />
              </div>

              {/* Live summary */}
              <div className="bg-[#0A0A0A] border border-[#1E293B] rounded-md p-4 space-y-1" data-testid="receipt-summary">
                {currency === 'MXN' && missingRates.length > 0 && (
                  <div className="mb-2 p-2 bg-amber-900/20 border border-amber-700/40 rounded text-xs text-amber-300" data-testid="receipt-missing-rates-warning">
                    ⚠ These invoices have no exchange rate set on their edition: {missingRates.join(', ')}. Enter a Custom Exchange Rate above or set the rate on the edition.
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-[#94A3B8]">Total invoiced ({selectedInvoiceIds.length} invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}):</span>
                  <span className="text-[#F8FAFC] font-medium" data-testid="receipt-total-invoiced">{formatMoney(totalInvoicedInCurrency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#94A3B8]">Amount paid:</span>
                  <span className="text-emerald-400 font-medium" data-testid="receipt-amount-summary">{formatMoney(amountPaidNum)}</span>
                </div>
                <div className="flex justify-between text-base pt-2 border-t border-[#1E293B]">
                  <span className={balance > 0.005 ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold'}>Balance due:</span>
                  <span className={balance > 0.005 ? 'text-red-400 font-bold' : 'text-emerald-400 font-bold'} data-testid="receipt-balance">{formatMoney(balance)}</span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Switch checked={sendByEmail} onCheckedChange={setSendByEmail} data-testid="receipt-send-email-toggle" />
                  Also email to {clientInvoices[0]?.advertiser_email || 'client'}
                </label>
                <div className="flex-1"></div>
                <Button
                  onClick={() => handleGenerate(true)}
                  disabled={!canSubmit || generating}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="receipt-generate-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {generating ? 'Generating…' : 'Generate & Download PDF'}
                </Button>
              </div>
            </>
          )}

          {/* Receipt History */}
          <div className="pt-4 border-t border-[#1E293B]">
            <button
              onClick={() => setShowHistory(h => !h)}
              className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#F8FAFC]"
              data-testid="receipt-history-toggle"
            >
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="font-medium">Receipt History ({receipts.length})</span>
            </button>
            {showHistory && (
              receiptsLoading ? (
                <p className="mt-3 text-sm text-[#94A3B8]">Loading…</p>
              ) : receipts.length === 0 ? (
                <p className="mt-3 text-sm text-[#94A3B8] italic">No receipts generated yet.</p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
                        <th className="py-2 px-3 text-left text-xs uppercase text-[#94A3B8]">Receipt #</th>
                        <th className="py-2 px-3 text-left text-xs uppercase text-[#94A3B8]">Client</th>
                        <th className="py-2 px-3 text-left text-xs uppercase text-[#94A3B8]">Date</th>
                        <th className="py-2 px-3 text-right text-xs uppercase text-[#94A3B8]">Amount</th>
                        <th className="py-2 px-3 text-right text-xs uppercase text-[#94A3B8]">Balance</th>
                        <th className="py-2 px-3 text-center text-xs uppercase text-[#94A3B8]">Method</th>
                        <th className="py-2 px-3 text-right text-xs uppercase text-[#94A3B8]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1E293B]">
                      {receipts.map(rc => (
                        <tr key={rc.id} className="hover:bg-[#1A1A1A]" data-testid={`receipt-row-${rc.id}`}>
                          <td className="py-2 px-3 font-mono text-emerald-400">{rc.receipt_number}</td>
                          <td className="py-2 px-3 text-[#F8FAFC]">{rc.advertiser_name}</td>
                          <td className="py-2 px-3 text-[#94A3B8]">{rc.payment_date}</td>
                          <td className="py-2 px-3 text-right text-[#F8FAFC]">
                            {rc.currency === 'MXN' ? 'MX$' : '$'}{(rc.amount_paid || 0).toFixed(2)}
                          </td>
                          <td className={`py-2 px-3 text-right ${rc.balance > 0.005 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {rc.currency === 'MXN' ? 'MX$' : '$'}{(rc.balance || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-3 text-center text-xs text-[#94A3B8] capitalize">{(rc.payment_method || 'cash').replace('_', ' ')}</td>
                          <td className="py-2 px-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadReceipt(rc)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-7 w-7 p-0" title="Download PDF" data-testid={`receipt-download-${rc.id}`}><Download className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleSendReceiptEmail(rc)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-7 w-7 p-0" title="Send email" data-testid={`receipt-email-${rc.id}`}><Mail className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteReceipt(rc)} className="text-[#94A3B8] hover:text-red-400 h-7 w-7 p-0" title="Delete" data-testid={`receipt-delete-${rc.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingPage;
